import React, { useEffect, useRef, useState } from "react";

const THUMB_SIZE_PX = 56;
const TRACK_INSET_PX = 6;
const CONFIRM_THRESHOLD = 0.88;
const CONFIRM_DELAY_MS = 220;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export default function SlideToConfirm({
  label,
  confirmedLabel,
  ariaLabel,
  disabled = false,
  onConfirm,
}) {
  const trackRef = useRef(null);
  const pointerIdRef = useRef(null);
  const pointerStartXRef = useRef(0);
  const progressStartRef = useRef(0);
  const confirmedRef = useRef(false);
  const confirmTimerRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  useEffect(
    () => () => {
      if (confirmTimerRef.current) {
        window.clearTimeout(confirmTimerRef.current);
      }
    },
    []
  );

  const getMaxTravel = () => {
    const trackWidth = trackRef.current?.getBoundingClientRect().width || 0;
    return Math.max(0, trackWidth - THUMB_SIZE_PX - TRACK_INSET_PX * 2);
  };

  const complete = (maxTravel = getMaxTravel()) => {
    if (disabled || confirmedRef.current || maxTravel <= 0) return;
    confirmedRef.current = true;
    setDragging(false);
    setConfirmed(true);
    setProgress(maxTravel);
    confirmTimerRef.current = window.setTimeout(() => {
      onConfirm?.();
    }, CONFIRM_DELAY_MS);
  };

  const reset = () => {
    if (confirmedRef.current) return;
    setDragging(false);
    setProgress(0);
  };

  const handlePointerDown = (event) => {
    if (disabled || confirmedRef.current) return;
    pointerIdRef.current = event.pointerId;
    pointerStartXRef.current = event.clientX;
    progressStartRef.current = progress;
    setDragging(true);
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handlePointerMove = (event) => {
    if (pointerIdRef.current !== event.pointerId || !dragging) return;
    const maxTravel = getMaxTravel();
    const nextProgress = clamp(
      progressStartRef.current + event.clientX - pointerStartXRef.current,
      0,
      maxTravel
    );
    setProgress(nextProgress);
  };

  const finishPointerGesture = (event) => {
    if (pointerIdRef.current !== event.pointerId) return;
    const maxTravel = getMaxTravel();
    const finalProgress = clamp(
      progressStartRef.current + event.clientX - pointerStartXRef.current,
      0,
      maxTravel
    );
    const shouldConfirm =
      maxTravel > 0 && finalProgress / maxTravel >= CONFIRM_THRESHOLD;
    pointerIdRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    if (shouldConfirm) {
      complete(maxTravel);
    } else {
      reset();
    }
  };

  const handlePointerCancel = (event) => {
    if (pointerIdRef.current !== event.pointerId) return;
    pointerIdRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    reset();
  };

  const handleKeyDown = (event) => {
    if (disabled || confirmedRef.current) return;
    const maxTravel = getMaxTravel();
    if (maxTravel <= 0) return;

    if (event.key === "End") {
      event.preventDefault();
      complete(maxTravel);
      return;
    }

    if (event.key === "Home") {
      event.preventDefault();
      setProgress(0);
      return;
    }

    if (event.key === "ArrowRight" || event.key === "ArrowUp") {
      event.preventDefault();
      setProgress((current) => clamp(current + maxTravel * 0.1, 0, maxTravel));
      return;
    }

    if (event.key === "ArrowLeft" || event.key === "ArrowDown") {
      event.preventDefault();
      setProgress((current) => clamp(current - maxTravel * 0.1, 0, maxTravel));
    }
  };

  const maxTravel = getMaxTravel();
  const progressPercent = maxTravel > 0 ? Math.round((progress / maxTravel) * 100) : 0;

  return (
    <div
      ref={trackRef}
      className="relative h-[68px] w-full overflow-hidden rounded-full border border-white/45 bg-black/20 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
      data-testid="slide-to-confirm-track"
    >
      <div
        aria-hidden="true"
        className="absolute inset-y-0 left-0 bg-white/12"
        style={{ width: `${TRACK_INSET_PX + THUMB_SIZE_PX + progress}px` }}
      />

      <span
        aria-hidden="true"
        className={`pointer-events-none absolute inset-0 flex items-center justify-center pl-[62px] pr-4 text-center text-[15px] font-semibold transition-opacity duration-150 ${
          confirmed ? "opacity-0" : "text-white/82"
        }`}
      >
        {label}
      </span>

      {confirmed ? (
        <span
          aria-live="polite"
          className="pointer-events-none absolute inset-0 flex items-center justify-center pr-[62px] text-[15px] font-semibold text-white"
        >
          {confirmedLabel}
        </span>
      ) : null}

      <div
        role="slider"
        tabIndex={disabled ? -1 : 0}
        aria-label={ariaLabel || label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={confirmed ? 100 : progressPercent}
        aria-disabled={disabled}
        onKeyDown={handleKeyDown}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={finishPointerGesture}
        onPointerCancel={handlePointerCancel}
        className={`absolute left-[6px] top-[6px] flex h-14 w-14 select-none items-center justify-center rounded-full bg-white text-[#172236] shadow-[0_6px_18px_rgba(0,0,0,0.24)] outline-none ring-offset-2 ring-offset-[#23303e] focus-visible:ring-2 focus-visible:ring-white ${
          disabled ? "cursor-not-allowed opacity-65" : "cursor-grab active:cursor-grabbing"
        } ${dragging ? "transition-none" : "transition-transform duration-200 ease-out"}`}
        style={{
          touchAction: "none",
          transform: `translate3d(${progress}px, 0, 0)`,
        }}
      >
        {confirmed ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M5 12.5L9.5 17L19 7.5"
              stroke="currentColor"
              strokeWidth="2.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        ) : (
          <svg width="25" height="25" viewBox="0 0 25 25" fill="none" aria-hidden="true">
            <path
              d="M7 5L14.5 12.5L7 20M14 5L21.5 12.5L14 20"
              stroke="currentColor"
              strokeWidth="2.25"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>
    </div>
  );
}
